import { pick } from '@exodus/basic-utils'
import { Timer } from '@exodus/timer'
import { url } from '@exodus/fetch/url'
import createFetchival from '@exodus/fetch/experimental/create-fetchival'
import delay from 'delay'
import EventEmitter from 'events/events.js'
import ms from 'ms'
import typeforce from '@exodus/typeforce'

import { batchProcessOrders, getOrderUpdates, shouldCheckOrder } from './utils/index.js'
import filterChanges from './filter-changes.js'
import {
  FAIL_LIMIT,
  ORDER_STATUS_UPDATE_STRATEGY,
  V3_ORDER_FINISHED_STATUSES,
} from './constants.js'

class OrdersMonitor extends EventEmitter {
  #appName
  #appVersion
  #appPlatform
  #assetsModule
  #availableAssetNamesAtom
  #baseUrl
  #txLogsAtom
  #isMoneroSyncing
  #logger
  #optimisticActivityEnabled
  #ordersAtom
  #timer
  #txTypes
  #fetchival
  #orderStatusUpdateStrategy
  #cachedFinishedV3Orders
  #restoreAtom

  constructor(opts) {
    super()

    typeforce(
      {
        assetsModule: 'Object',
        availableAssetNamesAtom: 'Object',
        txLogsAtom: 'Object',
        config: {
          appName: 'String',
          appVersion: 'String',
          appPlatform: '?String',
          baseUrl: 'String',
          interval: 'Number',
          optimisticActivityEnabled: 'Boolean',
          orderStatusUpdateStrategy: typeforce.maybe(
            typeforce.oneOf(
              ...Object.values(ORDER_STATUS_UPDATE_STRATEGY).map((v) => typeforce.value(v))
            )
          ),
        },
        isMoneroSyncing: '?Function',
        logger: 'Object',
        ordersAtom: 'Object',
        fetch: 'Function',
      },
      opts
    )

    this.#appName = opts.config.appName
    this.#appVersion = opts.config.appVersion
    this.#appPlatform = opts.config.appPlatform
    this.#assetsModule = opts.assetsModule
    this.#availableAssetNamesAtom = opts.availableAssetNamesAtom
    this.#baseUrl = new URL(opts.config.baseUrl)
    this.#txLogsAtom = opts.txLogsAtom
    this.#isMoneroSyncing = opts.isMoneroSyncing ?? (async () => false)
    this.#logger = opts.logger
    this.#optimisticActivityEnabled = opts.config.optimisticActivityEnabled
    this.#ordersAtom = opts.ordersAtom
    this.#timer = new Timer(opts.config.interval)
    this.#txTypes = opts.config.txTypes
    this.#fetchival = createFetchival({ fetch: opts.fetch })
    this.#orderStatusUpdateStrategy =
      opts.config.orderStatusUpdateStrategy || ORDER_STATUS_UPDATE_STRATEGY.CLIENT_AND_SERVER
    this.#cachedFinishedV3Orders = new Map()
    this.#restoreAtom = opts.restoreAtom
  }

  start = () => {
    this.startTime = new Date()
    this.#timer.start(this._tick)
  }

  tick = () => this.#timer.tick()

  _tick = async () => {
    const ordersToCheck = await this.#getOrdersToCheck()

    const v3Orders = await batchProcessOrders(
      ordersToCheck.filter((order) => !this.#cachedFinishedV3Orders.has(order.orderId)),
      async (batch) => {
        try {
          return await this.#request(`orders`).get({
            providerOrderIds: batch.map((order) => order.orderId),
          })
        } catch (err) {
          this.#logger.error(`Failed to get v3 orders: ${err}`)

          return []
        }
      }
    )

    const isRestoringAssets = await this.#restoreAtom.get()

    if (ordersToCheck.length === 0 && v3Orders.length === 0) {
      this.#cachedFinishedV3Orders.clear()
    }

    const ordersChangesPromises = ordersToCheck.map(async (order) => {
      const changes = Object.create(null)

      try {
        const v3Order =
          v3Orders.find((v3Order) => v3Order.providerOrderId === order.orderId) ||
          this.#cachedFinishedV3Orders.get(order.orderId)
        if (v3Order) {
          if (v3Order.message && v3Order.message !== order.message) {
            changes.message = v3Order.message
          }

          const toTx = await this.#findToTx(order)
          const isIndexlessOrder = await this.#getIsIndexlessOrder(order)

          const finished = V3_ORDER_FINISHED_STATUSES.has(v3Order.status)
          if (finished) {
            this.#cachedFinishedV3Orders.set(order.orderId, v3Order)
          } else {
            const isExpired = await this.#isOrderExpired(order)
            if (isExpired) {
              changes.status = 'failed-final'
            }
          }

          const updates = getOrderUpdates({
            v3Order,
            toTx,
            isIndexlessOrder,
            txTypes: this.#txTypes,
            order,
            orderStatusUpdateStrategy: this.#orderStatusUpdateStrategy,
            isRestoringAssets,
            toAsset: this.#assetsModule.getAsset(order.toAsset),
          })

          Object.assign(changes, updates)
        } else {
          changes.status = 'failed-final'
        }
      } catch (err) {
        this.#logger.warn('Orders monitor fetchOrderData error', order, err.message, err.stack)
      }

      await delay(ms('250ms'))

      const filteredChanges = filterChanges({
        order,
        changes,
        optimisticActivityEnabled: this.#optimisticActivityEnabled,
      })

      return { order, changes: filteredChanges }
    })

    const ordersChanges = await Promise.all(ordersChangesPromises)

    const changes = ordersChanges.filter(({ changes }) => Object.keys(changes).length > 0)

    if (changes.length === 0) return

    this.emit('orders-bulk-update', changes)
  }

  #getIsIndexlessOrder = async (order) => {
    const toAsset = this.#assetsModule.getAsset(order.toAsset)

    return toAsset?.baseAsset.api?.hasFeature?.('noHistory')
  }

  #findTx = async ({ txId, walletAccount, assetName }) => {
    if (!assetName || !txId) return null

    const { value: txLogs } = await this.#txLogsAtom.get()
    return txLogs[walletAccount]?.[assetName]?.get(txId) || null
  }

  #findToTx = async (order) => {
    return this.#findTx({
      txId: order.toTxId,
      assetName: order.toAsset,
      walletAccount: order.toWalletAccount,
    })
  }

  #getOrdersToCheck = async () => {
    const orders = await this.#ordersAtom.get()
    const availableAssets = await this.#getAvailableAssets()
    return [...orders].filter((order) => shouldCheckOrder({ order, availableAssets }))
  }

  #getAvailableAssets = async () =>
    pick(this.#assetsModule.getAssets(), await this.#availableAssetNamesAtom.get())

  #request = (path) =>
    this.#fetchival(url`${this.#baseUrl}/${path}`, {
      headers: {
        'App-Name': this.#appName,
        'App-Version': this.#appVersion,
        ...(this.#appPlatform ? { 'App-Platform': this.#appPlatform } : {}),
      },
    })

  #isOrderExpired = async (order) => {
    const now = new Date()
    if (order.status !== 'complete-verified' && now - order.date > FAIL_LIMIT) {
      const moneroSyncInProgress = await this.#isMoneroSyncing()
      if (!(order.toAsset === 'monero' && moneroSyncInProgress)) {
        return true
      }
    }

    return false
  }
}

const createOrdersMonitor = (args) => new OrdersMonitor({ ...args })

const ordersMonitorDefinition = {
  id: 'ordersMonitor',
  type: 'monitor',
  factory: createOrdersMonitor,
  dependencies: [
    'assetsModule',
    'availableAssetNamesAtom',
    'txLogsAtom',
    'config',
    'logger',
    'fetch',
    'ordersAtom',
    'isMoneroSyncing?',
    'restoreAtom',
  ],
  public: true,
}

export default ordersMonitorDefinition
